import { BaseOrder } from "./BaseOrder";
import { OrderId } from "./OrderId";
import { OrderResponse } from "./OrderResponse";
import { OrderStatus } from "./OrderStatus";
import { OrderTypes } from "./OrderTypes";
import { OrderIdentityVerificationRequest } from "./Order";
import { File } from "src/contexts/documentationArea/domain/File";
import { IdentityVerificationDocumentSides } from "src/contexts/identityVerification/domain/IdentityVerificationDocumentSides";
import { DocumentationAreaActorOrderRetrievalMethod } from "./DocumentationAreaActorOrderRetrievalMethod";

export type OrderIdentityVerificationParams = {
  id: OrderId;
  request: OrderIdentityVerificationRequest;
  responses: OrderResponse[];
  retrievalMethods: DocumentationAreaActorOrderRetrievalMethod[];
  status: OrderStatus;
};

export type OrderIdentityVerificationCreateParams = {
  id?: OrderId;
  request: OrderIdentityVerificationRequest;
};

export class OrderIdentityVerification extends BaseOrder<OrderIdentityVerificationRequest> {
  constructor({
    id,
    request,
    responses,
    retrievalMethods,
    status,
  }: OrderIdentityVerificationParams) {
    super(id, OrderTypes.IDENTITY_VERIFICATION, request, responses, retrievalMethods, status);
  }

  public getStagedFrontSide(): File | null {
    return this.request.data.stagedState.files.front;
  }

  public getStagedBackSide(): File | null {
    return this.request.data.stagedState.files.back;
  }

  public getStagedFileFromSide(side: "front" | "back"): File | null {
    return side === "front" ? this.getStagedFrontSide() : this.getStagedBackSide();
  }

  public setFrontLoading(): void {
    this.request.data.stagedState.frontLoading = true;
  }

  public setBackLoading(): void {
    this.request.data.stagedState.backLoading = true;
  }

  public setSideLoading(side: IdentityVerificationDocumentSides): void {
    if (side === "front") {
      this.setFrontLoading();
    } else {
      this.setBackLoading();
    }
  }

  public isFrontLoading(): boolean {
    return this.request.data.stagedState.frontLoading ?? false;
  }

  public isBackLoading(): boolean {
    return this.request.data.stagedState.backLoading ?? false;
  }

  public isSideLoading(side: IdentityVerificationDocumentSides): boolean {
    return side === "front" ? this.isFrontLoading() : this.isBackLoading();
  }

  static fromPrimitives(data: any): OrderIdentityVerification {
    return new OrderIdentityVerification({
      id: data.id,
      request: data.request,
      responses:
        data.responses?.map(
          (response: any) =>
            new OrderResponse({
              id: response.id,
              createdAt: new Date(response.createdAt),
              inputFiles: response.inputFiles || [],
              outputFiles: response.outputFiles || [],
              status: response.status,
              statusMessages: response.statusMessages || [],
              retrievalMethodOutput: response.retrievalMethodOutput || null,
            }),
        ) || [],
      retrievalMethods:
        data.retrievalMethods?.map(
          (method: any) =>
            new DocumentationAreaActorOrderRetrievalMethod({
              name: method.name,
              config: method.config || null,
            }),
        ) || [],
      status: data.status,
    });
  }
}
